import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import cogoToast from "cogo-toast";
import { FaArrowLeft } from "react-icons/fa";
import {
  createWashProjectApi,
  getWashProjectDetailsApi,
  updateWashProjectApi,
} from "../../../services/Api";
import { SUCCESS_RESULT } from "../../../Constants";
import {
  BENEFICIARY_TYPE_OPTIONS,
  buildWashPayload,
  canCreateWashProject,
  canUpdateWashProject,
  EMPTY_WASH_PROJECT,
  FUNCTIONALITY_OPTIONS,
  recordToWashForm,
  RESPONSE_TYPE_OPTIONS,
  WATER_QUALITY_OPTIONS,
  WATER_SOURCE_OPTIONS,
  WORK_TYPE_OPTIONS,
} from "./washProjectHelpers";
import "./WashProjects.css";

const Field = ({ label, required, children }) => (
  <FormGroup>
    <Label className={`wash-label${required ? " wash-required" : ""}`}>{label}</Label>
    {children}
  </FormGroup>
);

const WashProjectForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const history = useHistory();
  const canCreate = canCreateWashProject();
  const canUpdate = canUpdateWashProject();

  const [form, setForm] = useState({ ...EMPTY_WASH_PROJECT });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit && !canUpdate) {
      cogoToast.error("You do not have permission to update WASH projects.", {
        hideAfter: 4,
      });
      history.push("/projects/wash");
      return;
    }
    if (!isEdit && !canCreate) {
      cogoToast.error("You do not have permission to create WASH projects.", {
        hideAfter: 4,
      });
      history.push("/projects/wash");
    }
  }, [isEdit, canCreate, canUpdate, history]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getWashProjectDetailsApi({ id })
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setForm(recordToWashForm(res.data.data || {}));
        } else {
          cogoToast.error(res.data?.message || "Failed to load project", {
            hideAfter: 5,
          });
          history.push("/projects/wash");
        }
      })
      .catch((err) => {
        setLoading(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading the project.",
          { hideAfter: 5 }
        );
        history.push("/projects/wash");
      });
  }, [id, isEdit, history]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const required = [
      ["project", "Project name"],
      ["year_established", "Year established"],
      ["water_source", "Water source"],
      ["work_type", "Work type"],
      ["region", "Region"],
      ["district", "District"],
      ["site_name", "Site name"],
    ];
    const missing = required
      .filter(([key]) => !String(form[key] || "").trim())
      .map(([, label]) => label);
    if (missing.length) {
      cogoToast.error(`Please fill required fields: ${missing.slice(0, 3).join(", ")}`, {
        hideAfter: 4,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = buildWashPayload(form);
    setSaving(true);

    const request = isEdit
      ? updateWashProjectApi({ id, ...payload })
      : createWashProjectApi(payload);

    request
      .then((res) => {
        setSaving(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(
            res.data.message || (isEdit ? "Updated successfully" : "Created successfully"),
            { hideAfter: 3 }
          );
          const created = Array.isArray(res.data.data)
            ? res.data.data[0]
            : res.data.data;
          const nextId = created?.id || id;
          if (nextId) {
            history.push(`/projects/wash/details/${nextId}`);
          } else {
            history.push("/projects/wash");
          }
        } else {
          cogoToast.error(res.data?.message || "Save failed", { hideAfter: 5 });
        }
      })
      .catch((err) => {
        setSaving(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong. Please try again.",
          { hideAfter: 5 }
        );
      });
  };

  if (loading) {
    return (
      <div className="container-xl mt-4 text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="container-xl mt-4 mb-5 wash-page">
      <div className="wash-page__header">
        <div>
          <Button color="link" className="px-0 mb-2" tag={Link} to="/projects/wash">
            <FaArrowLeft className="me-1" /> Back to Wash Projects
          </Button>
          <h1 className="wash-page__title">
            {isEdit ? "Edit WASH Project" : "Create Project Entry"}
          </h1>
          <p className="wash-page__subtitle">
            Capture WASH infrastructure details, location, beneficiaries and budget.
          </p>
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="wash-card">
        <div className="wash-card__body">
          <div className="wash-form-section">
            <h2 className="wash-form-section__title">Project identity</h2>
            <p className="wash-form-section__desc">
              Core naming and classification for this WASH asset or intervention.
            </p>
            <Row>
              <Col md={8}>
                <Field label="Project name" required>
                  <Input name="project" value={form.project} onChange={handleChange} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Year established" required>
                  <Input
                    name="year_established"
                    value={form.year_established}
                    onChange={handleChange}
                    placeholder="e.g. 2024"
                  />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Water source" required>
                  <Input
                    type="select"
                    name="water_source"
                    value={form.water_source}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {WATER_SOURCE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Work type" required>
                  <Input
                    type="select"
                    name="work_type"
                    value={form.work_type}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {WORK_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Program area">
                  <Input
                    name="program_area"
                    value={form.program_area}
                    onChange={handleChange}
                  />
                </Field>
              </Col>
              <Col md={12}>
                <Field label="Details">
                  <Input
                    type="textarea"
                    rows={3}
                    name="details"
                    value={form.details}
                    onChange={handleChange}
                    placeholder="Describe the intervention…"
                  />
                </Field>
              </Col>
            </Row>
          </div>

          <div className="wash-form-section">
            <h2 className="wash-form-section__title">Technical details</h2>
            <p className="wash-form-section__desc">
              Physical and operational attributes of the water point or facility.
            </p>
            <Row>
              <Col md={4}>
                <Field label="Depth">
                  <Input
                    name="depth"
                    value={form.depth}
                    onChange={handleChange}
                    placeholder="e.g. 120m"
                  />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Yield / volume">
                  <Input
                    name="yield_or_volume"
                    value={form.yield_or_volume}
                    onChange={handleChange}
                    placeholder="e.g. 8 m3/hr"
                  />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Water quality">
                  <Input
                    type="select"
                    name="water_quality"
                    value={form.water_quality}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {WATER_QUALITY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Functionality">
                  <Input
                    type="select"
                    name="functionality"
                    value={form.functionality}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {FUNCTIONALITY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Donor">
                  <Input name="donor" value={form.donor} onChange={handleChange} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Budget value">
                  <Input
                    type="number"
                    step="0.01"
                    name="budget_value"
                    value={form.budget_value}
                    onChange={handleChange}
                  />
                </Field>
              </Col>
            </Row>
          </div>

          <div className="wash-form-section">
            <h2 className="wash-form-section__title">Location</h2>
            <p className="wash-form-section__desc">
              Geographic placement used for filtering and mapping.
            </p>
            <Row>
              <Col md={4}>
                <Field label="Region" required>
                  <Input name="region" value={form.region} onChange={handleChange} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="District" required>
                  <Input name="district" value={form.district} onChange={handleChange} />
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Site name" required>
                  <Input name="site_name" value={form.site_name} onChange={handleChange} />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Latitude">
                  <Input
                    type="number"
                    step="any"
                    name="latitude"
                    value={form.latitude}
                    onChange={handleChange}
                  />
                </Field>
              </Col>
              <Col md={6}>
                <Field label="Longitude">
                  <Input
                    type="number"
                    step="any"
                    name="longitude"
                    value={form.longitude}
                    onChange={handleChange}
                  />
                </Field>
              </Col>
            </Row>
          </div>

          <div className="wash-form-section">
            <h2 className="wash-form-section__title">Beneficiaries & notes</h2>
            <p className="wash-form-section__desc">
              Who benefits from this intervention and any handover remarks.
            </p>
            <Row>
              <Col md={4}>
                <Field label="Beneficiary type">
                  <Input
                    type="select"
                    name="beneficiary_type"
                    value={form.beneficiary_type}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {BENEFICIARY_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Response type">
                  <Input
                    type="select"
                    name="response_type"
                    value={form.response_type}
                    onChange={handleChange}
                  >
                    <option value="">Select…</option>
                    {RESPONSE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </Field>
              </Col>
              <Col md={4}>
                <Field label="Total beneficiaries">
                  <Input
                    type="number"
                    min={0}
                    name="total_beneficiaries"
                    value={form.total_beneficiaries}
                    onChange={handleChange}
                  />
                </Field>
              </Col>
              <Col md={12}>
                <Field label="Remarks">
                  <Input
                    type="textarea"
                    rows={3}
                    name="remarks"
                    value={form.remarks}
                    onChange={handleChange}
                  />
                </Field>
              </Col>
            </Row>
          </div>

          <div className="wash-footer">
            <Button color="secondary" outline tag={Link} to="/projects/wash" disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" /> Saving…
                </>
              ) : isEdit ? (
                "Update project"
              ) : (
                "Create project entry"
              )}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default WashProjectForm;
